import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaUser, FaEnvelope, FaLock, FaPhone, FaCar, FaIdCard, FaCalendarAlt, FaSpinner, FaTaxi, FaExclamationCircle, FaEye, FaEyeSlash, FaArrowLeft, FaCheckCircle, FaClipboardList, FaFileUpload } from 'react-icons/fa';
import { registerDriver } from '../../features/driver/driverAuthSlice';
import { useDocumentRequirements } from '../../context/DocumentRequirementsContext';
import { API_URL } from '../../config/apiConfig';

const DriverRegister = () => {
  const [cabTypes, setCabTypes] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loadingCabTypes, setLoadingCabTypes] = useState(false);
  const [promoImages, setPromoImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { documentConfig, loading: loadingDocConfig, error: docConfigError } = useDocumentRequirements();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    vehicleModel: '',
    vehicleNumber: '',
    licenseNumber: '',
    licenseExpiry: '',
    vehicleType: '',
    aadhaarCard: null,
    driversLicense: null,
    driverPhoto: null,
    vehicleRC: null,
    insuranceCertificate: null,
    pucCertificate: null,
    fitnessCertificate: null,
    routePermit: null,
    vehiclePhotoFront: null,
    vehiclePhotoBack: null
  });
  
  const { 
    name, email, phone, password, confirmPassword, 
    vehicleModel, vehicleNumber, vehicleType, licenseNumber, licenseExpiry 
  } = formData;
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, isError, isSuccess, message } = useSelector((state) => state.driverAuth);
  
  // Fetch promotional images
  useEffect(() => {
    const fetchPromoImages = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth-images`, {
          params: { type: 'driver_register' }
        });
        if (response.data.success && response.data.data.length > 0) {
          setPromoImages(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching images:", error);
      }
    };
    fetchPromoImages();
  }, []);

  // Image rotation
  useEffect(() => {
    if (promoImages.length > 1) {
      const timer = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % promoImages.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [promoImages]);

  useEffect(() => {
    const fetchCabTypes = async () => {
      setLoadingCabTypes(true);
      try {
        const response = await axios.get(`${API_URL}/cabtype/all`);
        if (response.data && response.data.data) {
          const activeCabTypes = response.data.data.filter(cabType => cabType.active);
          setCabTypes(activeCabTypes);
        }
      } catch (error) {
        try {
          const fallbackResponse = await axios.get(`${API_URL}/cab`);
          if (fallbackResponse.data && fallbackResponse.data.data) {
            setCabTypes(fallbackResponse.data.data);
          }
        } catch (fallbackError) {
          toast.error('Failed to load available cab types.');
        }
      } finally {
        setLoadingCabTypes(false);
      }
    };
    fetchCabTypes();
  }, []);
  
  useEffect(() => {
    if (isError) toast.error(message);
    if (isSuccess) {
      toast.success("Application submitted successfully!");
      navigate('/driver/login');
    }
  }, [isError, isSuccess, message, navigate]);
  
  const onChange = (e) => {
    const documentTypes = [
      'aadhaarCard', 'driversLicense', 'driverPhoto',
      'vehicleRC', 'insuranceCertificate', 'pucCertificate',
      'fitnessCertificate', 'routePermit', 'vehiclePhotoFront', 'vehiclePhotoBack'
    ];
    
    if (documentTypes.includes(e.target.name)) {
      const file = e.target.files[0];
      if (file) {
        if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
          toast.error(`Only image files are allowed for ${e.target.name}`);
          e.target.value = '';
          return;
        }
        setFormData((prevState) => ({ ...prevState, [e.target.name]: file }));
      }
    } else {
      setFormData((prevState) => ({ ...prevState, [e.target.name]: e.target.value }));
    }
  };
  
  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    
    const requiredDocKeys = Object.entries(documentConfig)
      .filter(([_, config]) => config.required)
      .map(([key]) => key);
    
    const missingDocs = requiredDocKeys.filter(key => !formData[key]);
    if (missingDocs.length > 0) {
      toast.error(`Missing documents: ${missingDocs.map(k => documentConfig[k].name).join(', ')}`);
      return;
    }
    
    if (!/^\d{10}$/.test(phone)) { toast.error('Invalid 10-digit phone number'); return; }
    if (!vehicleType) { toast.error('Select vehicle type'); return; }
    
    const driverData = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key]) driverData.append(key, formData[key]);
    });
    
    dispatch(registerDriver(driverData));
    toast.info('Submitting your driver registration...');
  };
  
  return (
    <div className="min-h-screen bg-[#F2F2F2] flex items-center justify-center p-4 py-12">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[1200px] flex flex-col md:flex-row overflow-hidden relative min-h-[700px]">
        
        {/* Left Side - Promo (Sticky-like) */}
        <div className="hidden md:block w-[35%] relative bg-[#000] overflow-hidden">
          {promoImages.length > 0 ? (
            <div className="h-full sticky top-0">
              <img
                src={promoImages[currentImageIndex].imageUrl}
                alt={promoImages[currentImageIndex].title}
                className="w-full h-full object-cover transition-opacity duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-12 left-10 right-10 text-white">
                <h3 className="text-3xl font-black mb-4 leading-tight">{promoImages[currentImageIndex].title}</h3>
                <p className="text-sm opacity-90 leading-relaxed font-medium">{promoImages[currentImageIndex].description}</p>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center"><FaSpinner className="animate-spin text-white text-4xl" /></div>
          )}
        </div>

        {/* Right Side - Form */}
        <div className="w-full md:w-[65%] p-8 md:p-12 overflow-y-auto max-h-[90vh]">
          <button 
            onClick={() => navigate("/driver/login")}
            className="text-gray-400 hover:text-blue-600 transition-colors flex items-center gap-2 text-xs font-black tracking-widest mb-8"
          >
            <FaArrowLeft /> BACK TO LOGIN
          </button>

          <header className="mb-12">
            <h1 className="text-4xl font-black text-gray-900 mb-2 tracking-tight">Driver Registration</h1>
            <p className="text-gray-500 font-bold tracking-wide uppercase text-[10px]">Start your journey with India's most trusted partner network</p>
          </header>

          <form onSubmit={onSubmit} className="space-y-12 animate-fadeIn">
            
            {/* Section 1: Identity */}
            <section>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600"><FaUser /></div>
                <h2 className="text-xl font-black text-gray-800 uppercase tracking-wider">Partner Identity</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                <div className="group">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 group-focus-within:text-blue-600">Full Name</label>
                  <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all pb-1">
                    <input type="text" name="name" value={name} onChange={onChange} className="w-full py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200" placeholder="John Doe" required />
                  </div>
                </div>
                <div className="group">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 group-focus-within:text-blue-600">Phone Number</label>
                  <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all pb-1">
                    <input type="tel" name="phone" value={phone} onChange={onChange} className="w-full py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200" placeholder="10-digit mobile" required />
                  </div>
                </div>
                <div className="group md:col-span-2">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 group-focus-within:text-blue-600">Email Address</label>
                  <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all pb-1">
                    <input type="email" name="email" value={email} onChange={onChange} className="w-full py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200" placeholder="partner@email.com" required />
                  </div>
                </div>
                <div className="group">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 group-focus-within:text-blue-600">New Password</label>
                  <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all pb-1">
                    <input type={showPassword ? 'text' : 'password'} name="password" value={password} onChange={onChange} className="w-full py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200" placeholder="Minimum 6 chars" required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-300">{showPassword ? <FaEyeSlash /> : <FaEye />}</button>
                  </div>
                </div>
                <div className="group">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 group-focus-within:text-blue-600">Confirm Password</label>
                  <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all pb-1">
                    <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={confirmPassword} onChange={onChange} className="w-full py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200" placeholder="Repeat password" required />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-300">{showConfirmPassword ? <FaEyeSlash /> : <FaEye />}</button>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2: Vehicle */}
            <section>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600"><FaCar /></div>
                <h2 className="text-xl font-black text-gray-800 uppercase tracking-wider">Vehicle & License</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                <div className="group">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 group-focus-within:text-blue-600">Vehicle Category</label>
                  <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all pb-1">
                    <select name="vehicleType" value={vehicleType} onChange={onChange} className="w-full py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none" required>
                      <option value="">Select Category</option>
                      {cabTypes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="group">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 group-focus-within:text-blue-600">Vehicle Model</label>
                  <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all pb-1">
                    <input type="text" name="vehicleModel" value={vehicleModel} onChange={onChange} className="w-full py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200" placeholder="e.g. Toyota Innova" required />
                  </div>
                </div>
                <div className="group">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 group-focus-within:text-blue-600">Registration Plate</label>
                  <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all pb-1">
                    <input type="text" name="vehicleNumber" value={vehicleNumber} onChange={onChange} className="w-full py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200" placeholder="GJ 01 XX 0000" required />
                  </div>
                </div>
                <div className="group">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 group-focus-within:text-blue-600">License Number</label>
                  <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all pb-1">
                    <input type="text" name="licenseNumber" value={licenseNumber} onChange={onChange} className="w-full py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200" placeholder="DL-12345678" required />
                  </div>
                </div>
                <div className="group">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 group-focus-within:text-blue-600">License Expiry</label>
                  <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all pb-1">
                    <input type="date" name="licenseExpiry" value={licenseExpiry} onChange={onChange} className="w-full py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none" required />
                  </div>
                </div>
              </div>
            </section>

            {/* Section 3: Documents */}
            <section className="bg-gray-50/50 p-8 rounded-3xl border border-gray-100">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600"><FaClipboardList /></div>
                <h2 className="text-xl font-black text-gray-800 uppercase tracking-wider">KYC Documents</h2>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {Object.entries(documentConfig).map(([key, config]) => (
                  <div key={key} className="relative group bg-white p-5 rounded-2xl border-2 border-dashed border-gray-200 hover:border-blue-600 transition-all active:scale-[0.98]">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[9px] font-black uppercase tracking-widest text-gray-400 group-hover:text-blue-600">{config.name}</span>
                      {config.required && <span className="text-[7px] font-black bg-red-50 text-red-500 px-1.5 py-0.5 rounded uppercase">Mandatory</span>}
                    </div>
                    <div className="flex flex-col items-center gap-2">
                       <FaFileUpload className={`text-2xl ${formData[key] ? 'text-green-500' : 'text-gray-200 group-hover:text-blue-500'}`} />
                       <span className="text-[10px] font-bold text-gray-500 truncate w-full text-center">
                         {formData[key] ? formData[key].name : 'Click to Upload'}
                       </span>
                    </div>
                    <input type="file" name={key} onChange={onChange} className="absolute inset-0 opacity-0 cursor-pointer" accept=".jpg,.jpeg,.png" required={config.required} />
                  </div>
                ))}
              </div>
            </section>

            <div className="pt-8 space-y-8">
               <div className="flex items-center gap-4 bg-gray-50 p-5 rounded-2xl">
                 <input type="checkbox" id="terms" required className="w-6 h-6 rounded-lg text-blue-600 border-gray-200 focus:ring-0" />
                 <label htmlFor="terms" className="text-[11px] font-bold text-gray-600 leading-relaxed">
                   I hereby authorize CabZigO to verify my documents and background. I agree to the <span className="text-blue-600 underline">Terms of Partnership</span>.
                 </label>
               </div>

               <button
                 type="submit"
                 disabled={isLoading}
                 className={`w-full py-6 rounded-full font-black text-xl tracking-[3px] shadow-xl transition-all active:scale-95 ${
                   isLoading ? "bg-gray-100 text-gray-400" : "bg-gradient-to-r from-blue-600 to-blue-800 text-white hover:shadow-2xl"
                 }`}
               >
                 {isLoading ? <FaSpinner className="animate-spin mx-auto" /> : "SUBMIT APPLICATION"}
               </button>
            </div>
          </form>

          <footer className="mt-16 pt-8 border-t border-gray-100 text-center">
             <p className="text-[10px] font-bold text-gray-300 uppercase tracking-[3px]">Partner Governance Portal • CabZigO © 2024</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default DriverRegister;
